// src/components/alerts/ViewActiveAlertModal.js
// --- UPDATE (Alert System AI) ------------------------------------------------
//   • NEW "FlowSense AI" section: the triage score and its reasons, plus "Explain this alert",
//     which asks FlowSense AI (Ollama) for likely cause and next steps using this alert's facts.
//   • NEW sensor readings chart (AlertReadingChart): readings, threshold, learned normal range.
//   • NEW footer actions: Acknowledge / Resolve with an optional note, right from the modal
//     (onAcknowledge / onResolve props; hidden when not provided or the alert is closed).
// --- END UPDATE --------------------------------------------------------------
import React, { useState, useEffect } from 'react'; // --- UPDATE (Alert System AI): + useState, useEffect
import PropTypes from 'prop-types';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    Button, Text, VStack, HStack, Badge, Divider, Box, useColorModeValue,
    Link as ChakraLink, Textarea, SimpleGrid, Icon, Flex
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { formatDate } from '../../utils/date';
import { 
    MdSensors, MdBuild, MdAccessTime, MdInfoOutline, 
    MdAssignment, MdWarning, MdFactCheck 
} from 'react-icons/md';
// --- UPDATE (Alert System AI)
import { MdAutoAwesome, MdCheckCircle, MdEditNote } from 'react-icons/md';
import { Input, Spinner, Tooltip } from '@chakra-ui/react';
import AlertReadingChart from './AlertReadingChart';
import { askFlowSenseAi } from '../../services/flowsenseAiService';
import { scoreColor } from './alertUtils';
// --- END UPDATE

function ViewActiveAlertModal({ isOpen, onClose, alert, triage, onAcknowledge, onResolve }) {
    // =========================================================================
    // 1. TOP LEVEL HOOKS (Must be called before any early returns)
    // =========================================================================
    
    // Base Theme Colors
    const textColor = useColorModeValue("gray.800", "white");
    const headingColor = useColorModeValue("gray.500", "gray.400");
    const sectionBg = useColorModeValue("gray.50", "gray.700");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const mutedText = useColorModeValue("gray.600", "gray.300");

    // Pre-computed dynamic severity colors for the header
    const headerRed = useColorModeValue('red.50', 'red.900');
    const headerOrange = useColorModeValue('orange.50', 'orange.900');
    const headerYellow = useColorModeValue('yellow.50', 'yellow.900');
    const headerGreen = useColorModeValue('green.50', 'green.900');
    const headerGray = useColorModeValue('gray.50', 'gray.800');

    // Misc UI component colors
    const textareaBg = useColorModeValue("white", "gray.800");
    const footerBg = useColorModeValue("gray.50", "gray.800");

    // --- UPDATE (Alert System AI)
    const aiBg = useColorModeValue('purple.50', 'whiteAlpha.100');
    const [note, setNote] = useState('');
    const [working, setWorking] = useState(false);
    const [aiAnswer, setAiAnswer] = useState(null);
    const [aiBusy, setAiBusy] = useState(false);
    const [aiError, setAiError] = useState(null);
    useEffect(() => { setNote(''); setAiAnswer(null); setAiError(null); }, [alert?.id]);

    const explain = async () => {
        if (!alert) return;
        setAiBusy(true); setAiError(null);
        try {
            const context = {
                alert: {
                    id: alert.id, severity: alert.severity, status: alert.status, message: alert.alert_message,
                    sensor: alert.sensor_name || alert.sensor_id, sensor_type: alert.sensor_type, sensor_location: alert.sensor_location,
                    asset: alert.asset_location_description || alert.asset_id, asset_type: alert.asset_type_name,
                    rule: alert.rule_name, last_value: alert.last_violation_value, triggered_at: alert.triggered_at,
                    acknowledged_at: alert.acknowledged_at, work_order: alert.work_order_id, work_order_status: alert.work_order_status,
                    notes: String(alert.notes || '').slice(-600),
                },
                ai_priority: triage || null,
            };
            const res = await askFlowSenseAi('Explain this alert for the duty supervisor: the most likely cause, how urgent it is, and the next 3 steps. Use only the facts given.', context, 'alerts');
            setAiAnswer(res?.answer || '');
        } catch (e) {
            setAiError(e?.message || 'FlowSense AI is unavailable right now.');
        } finally {
            setAiBusy(false);
        }
    };
    const runAction = async (fn) => {
        setWorking(true);
        try { const ok = await fn([alert.id], note.trim() || undefined); if (ok !== false) onClose(); } finally { setWorking(false); }
    };
    // --- END UPDATE

    // =========================================================================
    // 2. EARLY RETURNS
    // =========================================================================
    if (!alert) return null;

    // =========================================================================
    // 3. HELPER FUNCTIONS
    // =========================================================================
    const getSeverityColorScheme = (severity) => {
        switch (severity?.toLowerCase()) {
            case 'critical': return 'red';
            case 'high': return 'orange';
            case 'medium': return 'yellow';
            case 'low': return 'green';
            default: return 'gray';
        }
    };

    const getStatusColorScheme = (status) => {
        switch (status?.toLowerCase()) {
            case 'active': return 'red';
            case 'acknowledged': return 'blue';
            case 'work_order_created': return 'purple';
            case 'resolved':
            case 'auto_resolved':
            case 'resolved_by_work_order': return 'green';
            default: return 'gray';
        }
    };

    // Safely returns a pre-computed string without calling a hook inside
    const getSeverityHeaderColor = (severity) => {
        switch (severity?.toLowerCase()) {
            case 'critical': return headerRed;
            case 'high': return headerOrange;
            case 'medium': return headerYellow;
            case 'low': return headerGreen;
            default: return headerGray;
        }
    };

    // Reusable component for aligned data rows
    const DetailRow = ({ label, value, isBadge, badgeScheme }) => (
        <Box>
            <Text fontSize="xs" fontWeight="bold" color={headingColor} textTransform="uppercase" letterSpacing="wide" mb={1}>
                {label}
            </Text>
            {isBadge ? (
                <Badge colorScheme={badgeScheme} fontSize="sm" px={2} py={0.5} borderRadius="md">
                    {value}
                </Badge>
            ) : (
                <Text fontSize="sm" color={textColor} fontWeight="500" noOfLines={2} wordBreak="break-word">
                    {value || 'N/A'}
                </Text>
            )}
        </Box>
    );

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
            <ModalOverlay backdropFilter="blur(3px)" />
            <ModalContent overflow="hidden" borderRadius="xl">
                {/* Dynamic Header based on Severity */}
                <Box bg={getSeverityHeaderColor(alert.severity)} borderBottomWidth="1px" borderColor={borderColor}>
                    <ModalHeader color={textColor} display="flex" alignItems="center" gap={2}>
                        <Icon as={MdWarning} color={`${getSeverityColorScheme(alert.severity)}.500`} />
                        Alert Details #{alert.id}
                    </ModalHeader>
                    <ModalCloseButton mt={1} />
                </Box>

                <ModalBody py={6}>
                    <VStack spacing={6} align="stretch">
                        
                        {/* --- TOP BANNER: MESSAGE --- */}
                        <Box p={4} bg={sectionBg} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                            <Flex align="flex-start" gap={3}>
                                <Icon as={MdInfoOutline} boxSize={5} color="blue.500" mt={0.5} />
                                <Box>
                                    <Text fontWeight="bold" color={textColor} mb={1}>Alert Message</Text>
                                    <Text color={mutedText} fontSize="md" whiteSpace="pre-wrap">
                                        {alert.alert_message}
                                    </Text>
                                </Box>
                            </Flex>
                        </Box>

                        {/* --- UPDATE (Alert System AI): FlowSense AI + readings --- */}
                        <Box p={4} bg={aiBg} borderRadius="lg">
                            <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
                                <HStack>
                                    <Icon as={MdAutoAwesome} color="purple.400" />
                                    <Text fontWeight="bold" color={textColor}>FlowSense AI</Text>
                                    {triage && (
                                        <Tooltip hasArrow label="AI priority 0–100">
                                            <Badge colorScheme={scoreColor(triage.score)} fontSize="sm">priority {triage.score}</Badge>
                                        </Tooltip>
                                    )}
                                </HStack>
                                <Button size="sm" colorScheme="purple" variant="outline" leftIcon={aiBusy ? <Spinner size="xs" /> : <MdAutoAwesome />} onClick={explain} isDisabled={aiBusy}>
                                    Explain this alert
                                </Button>
                            </Flex>
                            {triage?.reasons?.length > 0 && (
                                <Text fontSize="sm" color={mutedText} mt={2}>Why: {triage.reasons.join(' · ')}</Text>
                            )}
                            {aiError && <Text fontSize="sm" color="orange.400" mt={2}>{aiError}</Text>}
                            {aiAnswer && <Text fontSize="sm" color={textColor} mt={3} whiteSpace="pre-wrap">{aiAnswer}</Text>}
                        </Box>
                        <Box p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                            <AlertReadingChart alertId={alert.id} />
                        </Box>
                        {/* --- END UPDATE --- */}

                        {/* --- PRIMARY STATS GRID --- */}
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                            <DetailRow 
                                label="Severity" 
                                value={alert.severity} 
                                isBadge 
                                badgeScheme={getSeverityColorScheme(alert.severity)} 
                            />
                            <DetailRow 
                                label="Status" 
                                value={alert.status?.replace(/_/g, ' ')} 
                                isBadge 
                                badgeScheme={getStatusColorScheme(alert.status)} 
                            />
                            <DetailRow 
                                label="Triggered At" 
                                value={formatDate(alert.triggered_at, true)} 
                            />
                        </SimpleGrid>

                        {/* --- ENTITY DETAILS SECTION --- */}
                        <Box>
                            <HStack mb={3}>
                                <Icon as={MdSensors} color={headingColor} />
                                <Text fontWeight="bold" fontSize="lg" color={textColor}>Associated Entities</Text>
                            </HStack>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} p={4} bg={sectionBg} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                                <DetailRow label="Sensor ID" value={alert.sensor_id} />
                                <DetailRow label="Sensor Name" value={alert.sensor_name} />
                                <DetailRow label="Sensor Type" value={alert.sensor_type} />
<DetailRow label="Sensor Location" value={alert.sensor_location || 'Location placement needed for sensor'} />                                <Box gridColumn={{ md: "span 2" }}><Divider my={2}/></Box>
                                <DetailRow label="Asset ID" value={alert.asset_id} />
                                <DetailRow label="Asset Type" value={alert.asset_type_name} />
                                <Box gridColumn={{ md: "span 2" }}>
                                    <DetailRow label="Asset Location" value={alert.asset_location_description} />
                                </Box>
                            </SimpleGrid>
                        </Box>

                        {/* --- WORKFLOW & RULES SECTION --- */}
                        <Box>
                            <HStack mb={3}>
                                <Icon as={MdAssignment} color={headingColor} />
                                <Text fontWeight="bold" fontSize="lg" color={textColor}>Workflow & Rules</Text>
                            </HStack>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                                <Box>
                                    <Text fontSize="xs" fontWeight="bold" color={headingColor} textTransform="uppercase" mb={1}>Triggering Rule</Text>
                                    {alert.rule_id ? (
                                        <Text fontSize="sm" color={textColor} fontWeight="500">
                                            #{alert.rule_id} — {alert.rule_name || 'N/A'}
                                        </Text>
                                    ) : (
                                        <Badge colorScheme="gray">Manual / External System</Badge>
                                    )}
                                </Box>
                                <Box>
                                    <Text fontSize="xs" fontWeight="bold" color={headingColor} textTransform="uppercase" mb={1}>Work Order Link</Text>
                                    {alert.work_order_id ? (
                                        <ChakraLink as={RouterLink} to={`/admin/workorders?search=${alert.work_order_id}`} color="teal.500" fontWeight="bold" display="flex" alignItems="center" gap={1}>
                                            <Icon as={MdBuild} /> 
                                            <Text>#{alert.work_order_id}</Text>
                                        </ChakraLink>
                                    ) : (
                                        <Text fontSize="sm" color={mutedText}>No Work Order Generated</Text>
                                    )}
                                </Box>
                                {alert.last_violation_value !== null && (
                                    <Box gridColumn={{ md: "span 2" }}>
                                        <DetailRow label="Last Recorded Violation Value" value={alert.last_violation_value} />
                                    </Box>
                                )}
                            </SimpleGrid>
                        </Box>

                        {/* --- TIMESTAMPS & NOTES SECTION --- */}
                        <Box>
                            <HStack mb={3}>
                                <Icon as={MdFactCheck} color={headingColor} />
                                <Text fontWeight="bold" fontSize="lg" color={textColor}>Resolution Timeline</Text>
                            </HStack>
                            <VStack align="stretch" spacing={4} p={4} bg={sectionBg} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                    <Box>
                                        <Text fontSize="xs" fontWeight="bold" color={headingColor} textTransform="uppercase" mb={1}>Acknowledged By</Text>
                                        {alert.acknowledged_at ? (
                                            <VStack align="start" spacing={0}>
                                                <Text fontSize="sm" color={textColor} fontWeight="bold">{alert.acknowledged_by_username || 'Unknown User'}</Text>
                                                <HStack color={mutedText} fontSize="xs">
                                                    <Icon as={MdAccessTime} />
                                                    <Text>{formatDate(alert.acknowledged_at, true)}</Text>
                                                </HStack>
                                            </VStack>
                                        ) : (
                                            <Text fontSize="sm" color={mutedText}>Pending Acknowledgement</Text>
                                        )}
                                    </Box>
                                    <Box>
                                        <Text fontSize="xs" fontWeight="bold" color={headingColor} textTransform="uppercase" mb={1}>Resolved By</Text>
                                        {alert.resolved_at ? (
                                            <VStack align="start" spacing={0}>
                                                <Text fontSize="sm" color={textColor} fontWeight="bold">{alert.resolved_by_username || 'System/Auto'}</Text>
                                                <HStack color={mutedText} fontSize="xs">
                                                    <Icon as={MdAccessTime} />
                                                    <Text>{formatDate(alert.resolved_at, true)}</Text>
                                                </HStack>
                                            </VStack>
                                        ) : (
                                            <Text fontSize="sm" color={mutedText}>Pending Resolution</Text>
                                        )}
                                    </Box>
                                </SimpleGrid>

                                {alert.notes && (
                                    <Box pt={2}>
                                        <Text fontSize="xs" fontWeight="bold" color={headingColor} textTransform="uppercase" mb={2}>System / Technician Notes</Text>
                                        <Textarea 
                                            value={alert.notes} 
                                            isReadOnly 
                                            bg={textareaBg}
                                            color={textColor} 
                                            minH="100px"
                                            borderColor={borderColor}
                                            _hover={{ borderColor: borderColor }}
                                        />
                                    </Box>
                                )}
                            </VStack>
                        </Box>

                    </VStack>
                </ModalBody>
                
                <ModalFooter borderTopWidth="1px" borderColor={borderColor} bg={footerBg} gap={2} flexWrap="wrap">
                    {/* --- UPDATE (Alert System AI): act from the modal */}
                    {(onAcknowledge || onResolve) && ['active', 'acknowledged', 'work_order_created'].includes(alert.status) && (
                        <>
                            <Input size="sm" flex="1" minW="200px" placeholder="Optional note" value={note} onChange={e => setNote(e.target.value)} maxLength={500} bg={textareaBg} />
                            {onAcknowledge && alert.status === 'active' && (
                                <Button size="sm" colorScheme="blue" leftIcon={<MdEditNote />} isLoading={working} onClick={() => runAction(onAcknowledge)}>Acknowledge</Button>
                            )}
                            {onResolve && (
                                <Button size="sm" colorScheme="green" leftIcon={<MdCheckCircle />} isLoading={working} onClick={() => runAction(onResolve)}>Resolve</Button>
                            )}
                        </>
                    )}
                    {/* --- END UPDATE */}
                    <Button onClick={onClose} variant="ghost" colorScheme="gray">
                        Close Window
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

ViewActiveAlertModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    alert: PropTypes.object,
    // --- UPDATE (Alert System AI)
    triage: PropTypes.shape({ score: PropTypes.number, reasons: PropTypes.array }),
    onAcknowledge: PropTypes.func,
    onResolve: PropTypes.func,
    // --- END UPDATE
};

export default ViewActiveAlertModal;